import React from "react";
import { ProvinceInterface, Provinces } from "constants/provinces";
import { Municipalities, MunicipalityInterface } from "constants/municipalities";
import { DistrictInterface, Districts } from "constants/districts";
import { isJSONString } from "./validate-json";

type OptionType = {
  label: string;
  value: string | number;
  key?: number | string;
};

const getProvinceId = (name: string) => {
  const result = Provinces?.filter((p: ProvinceInterface) => p?.name === name);
  return result?.[0]?.id;
};

const getDistrictId = (name: string) => {
  const result = Districts?.filter((p: DistrictInterface) => p?.name === name);
  return result?.[0]?.id;
};

const filterWardNumber = (municipality: string) => {
  const result = Municipalities?.filter((p: MunicipalityInterface) => p?.name === municipality);
  return result?.[0]?.wards;
};

const getWardNumbers = (municipality: string): number => {
  return isJSONString(municipality)
    ? JSON?.parse(municipality)?.wards
    : filterWardNumber(municipality);
};

export interface Address {
  district: string;
  province: string;
  municipality: string;
  ward: string;
}

export const initialState: Address = {
  district: "",
  province: "",
  municipality: "",
  ward: "",
};

const useAddressHelper = () => {
  const [address, setAddress] = React.useState(initialState);

  const getProvinceOptions = () => {
    const options = Provinces?.map((p: ProvinceInterface) => {
      return { label: p?.name, value: p?.name, key: p?.id };
    });
    return options;
  };

  const getDistrictOptions = () => {
    const filteredDistricts = address.province
      ? isJSONString(address.province)
        ? Districts?.filter((d) => d?.province_id === JSON?.parse(address.province)?.id)
        : Districts?.filter((d) => d?.province_id === getProvinceId(address.province))
      : Districts; // Return all districts if no province is selected

    const options = filteredDistricts?.map((o) => {
      return { label: o?.name, value: JSON?.stringify(o) };
    });

    return options;
  };

  // New function to get districts by province
  const getDistrictByProvince = (provinceName: string) => {
    const provinceId = getProvinceId(provinceName);
    const filteredDistricts = Districts?.filter((d) => d?.province_id === provinceId);
    
    const options = filteredDistricts?.map((o) => {
      return { label: o?.name, value: JSON?.stringify(o) };
    });
    
    return options;
  };
  
  // New function to get province from district
  const getProvinceFromDistrict = (districtName: string) => {
    const district = Districts?.find((d) => d?.name === districtName);
    if (!district) return null;
    
    const province = Provinces?.find((p) => p?.id === district.province_id);
    return province?.name || null;
  };

  const getMunicipalityOptions = () => {
    const filteredMunicipality = address.district
      ? isJSONString(address.district)
        ? Municipalities?.filter((d) => d?.district_id === JSON.parse(address.district)?.id)
        : Municipalities?.filter((d) => d?.district_id === getDistrictId(address.district))
      : [];

    const options = filteredMunicipality?.map((o) => {
      return { label: o?.name, value: JSON?.stringify(o) };
    });

    return options;
  };

  const getWardOptions = () => {
    const options: OptionType[] = [];

    if (address.municipality) {
      for (let i = 0; i < getWardNumbers(address.municipality); i++) {
        options.push({
          label: `${i + 1}`,
          value: i + 1,
        });
      }
    }

    return options;
  };

  return {
    getProvinceOptions,
    getDistrictOptions,
    getMunicipalityOptions,
    getWardOptions,
    getDistrictByProvince,
    getProvinceFromDistrict,
    address,
    setAddress,
  };
};

export { useAddressHelper };